import { type ReactNode } from "react";
import { useAuth } from "../lib/auth";
import BackgroundLayer from "./BackgroundLayer";
import MagicLinkLogin from "./MagicLinkLogin";

interface Props {
  children: ReactNode;
}

/**
 * Auth gate. Wraps any subtree that requires a Supabase session (which drives
 * RLS). Cloudflare Access was removed; Supabase Auth + RLS is the sole gate.
 *
 * States:
 *   - loading      → brief verifying screen
 *   - needs_login  → no Supabase session yet → magic-link form
 *   - authenticated→ session present, renders children (RLS gates the data)
 */
function RequireAuth({ children }: Props) {
  const state = useAuth();

  if (state.status === "loading") {
    return <Verifying />;
  }

  if (state.status === "needs_login") {
    return <MagicLinkLogin defaultEmail="" />;
  }

  return <>{children}</>;
}

function Verifying() {
  return (
    <div className="relative min-h-screen text-bone">
      <BackgroundLayer variant="modes" overlay="heavy" />
      <main className="relative z-10 flex min-h-screen flex-col items-center justify-center px-6 text-center">
        <p className="font-display text-sm font-light uppercase tracking-[0.18em] text-bone-mute">
          verifying credentials
        </p>
        <div className="mt-6 flex items-center gap-3">
          <span className="inline-block h-1.5 w-1.5 animate-pulse-ember rounded-full bg-ember-bright shadow-ember-glow" />
          <span className="inline-block h-1.5 w-1.5 animate-pulse-ember rounded-full bg-ember-bright shadow-ember-glow [animation-delay:200ms]" />
          <span className="inline-block h-1.5 w-1.5 animate-pulse-ember rounded-full bg-ember-bright shadow-ember-glow [animation-delay:400ms]" />
        </div>
      </main>
    </div>
  );
}

export default RequireAuth;
